import React, { useState, useEffect } from 'react';

const VisualInspectTab = ({ apiUrl }) => {
  const [inspections, setInspections] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInspections();
  }, []);

  const fetchInspections = async () => {
    try {
      const res = await fetch(`${apiUrl}/public/visual/inspections`);
      if (!res.ok) return;
      const data = await res.json();
      if (data.success && data.inspections?.length > 0) {
        setInspections(data.inspections);
        setSelectedIndex(0);
      }
    } catch (err) {
      console.error('Failed to fetch inspections:', err);
    } finally {
      setLoading(false);
    }
  };

  const analysis = inspections[selectedIndex] || null;

  const SeverityBadge = ({ level }) => {
    const colors = {
      mild: 'bg-yellow-500/20 text-yellow-400 border-yellow-500',
      moderate: 'bg-orange-500/20 text-orange-400 border-orange-500',
      severe: 'bg-red-500/20 text-red-400 border-red-500',
      low: 'bg-green-500/20 text-green-400 border-green-500',
      medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500',
      high: 'bg-red-500/20 text-red-400 border-red-500',
      critical: 'bg-red-600/30 text-red-300 border-red-600'
    };
    return (
      <span className={`text-[10px] px-1.5 py-0.5 rounded border font-bold ${colors[level] || 'bg-[#00ff4120] text-[#00ff41] border-[#00ff41]'}`}>
        {level?.toUpperCase()}
      </span>
    );
  };

  const UrgencyBadge = ({ level }) => {
    const colors = {
      now: 'bg-red-600 text-white',
      this_week: 'bg-orange-500 text-white',
      next_week: 'bg-yellow-500 text-black',
      not_yet: 'bg-[#00ff41] text-[#0a0e1a]'
    };
    return (
      <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${colors[level] || 'bg-[#00ff4120] text-[#00ff41]'}`}>
        {level?.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="terminal-border rounded-lg p-8 text-center">
        <div className="text-2xl mb-3 animate-pulse">🌿</div>
        <div className="text-sm text-[#00ff4180]">Loading inspections...</div>
      </div>
    );
  }

  if (inspections.length === 0) {
    return (
      <div className="terminal-border rounded-lg p-8 text-center" style={{ borderColor: '#9c27b040' }}>
        <div className="text-4xl mb-3">🔍</div>
        <div className="text-sm text-[#00ff4180] mb-1">No visual inspections yet</div>
        <div className="text-[10px] text-[#00ff4140]">
          Inspections are performed by the grower — results appear here automatically
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Selector Bar — matches Daily Report style */}
      <div className="terminal-border rounded-lg p-4" style={{ borderColor: '#9c27b0' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" style={{ color: '#9c27b0' }}>
              <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
            </svg>
            <h2 className="font-bold" style={{ color: '#9c27b0' }}>— VISUAL INSPECTION —</h2>
          </div>
          <select
            value={selectedIndex}
            onChange={(e) => setSelectedIndex(Number(e.target.value))}
            className="bg-[#0a0e1a] border border-[#9c27b0] text-[#00ff41] rounded px-3 py-2 text-sm font-mono cursor-pointer"
            style={{ minWidth: '300px' }}
          >
            {inspections.map((insp, i) => {
              const date = new Date(insp.timestamp).toLocaleDateString();
              const time = new Date(insp.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
              return (
                <option key={i} value={i}>
                  {date} {time} — Day {insp.growDay} ({insp.stage}) — {insp.health_score}%
                </option>
              );
            })}
          </select>
        </div>
      </div>

      {/* Info Bar — date, day, stage, score */}
      {analysis && (
        <div className="terminal-border rounded-lg overflow-hidden" style={{ borderColor: '#9c27b0' }}>
          <div className="px-4 py-2 flex items-center justify-between" style={{ backgroundColor: '#9c27b015' }}>
            <div className="flex items-center gap-6 text-sm">
              <span>
                <span className="text-[#00ff4180] text-xs mr-1">📅</span>
                {new Date(analysis.timestamp).toLocaleDateString()}
              </span>
              <span>
                <span className="text-[#00ff4180] text-xs mr-1">🌱</span>
                Day {analysis.growDay}
              </span>
              <span>
                <span className="text-[#00ff4180] text-xs mr-1">📊</span>
                {analysis.stage?.toUpperCase()}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[#00ff4180] text-xs">HEALTH:</span>
              <span className="font-bold text-lg terminal-glow">{analysis.health_score}%</span>
              <div className="w-20 h-2 bg-[#00ff4120] rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${analysis.health_score}%`,
                    background: analysis.health_score > 80 ? '#00ff41'
                      : analysis.health_score > 60 ? '#ffeb3b'
                      : analysis.health_score > 40 ? '#ffa500'
                      : '#ff0000'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {analysis && (
        <>
          {/* Health Summary */}
          <div className="terminal-border rounded-lg p-4" style={{ borderColor: '#9c27b0' }}>
            <h3 className="font-bold mb-3" style={{ color: '#9c27b0' }}>HEALTH ASSESSMENT</h3>
            <p className="text-sm leading-relaxed">{analysis.health_summary}</p>
            {analysis.next_inspection && (
              <div className="mt-2 text-[10px] text-[#00ff4180]">
                📅 Next inspection recommended: {analysis.next_inspection}
              </div>
            )}
          </div>

          {/* Deficiencies */}
          {analysis.issues?.deficiencies?.length > 0 && (
            <div className="terminal-border rounded-lg p-4" style={{ borderColor: '#ffa500' }}>
              <h3 className="font-bold text-[#ffa500] mb-3 flex items-center gap-2">
                ⚠️ NUTRIENT / DEFICIENCY ISSUES
              </h3>
              <div className="space-y-3">
                {analysis.issues.deficiencies.map((def, i) => (
                  <div key={i} className="bg-[#ffa50010] border border-[#ffa50040] rounded p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-bold text-sm text-[#ffa500]">{def.type}</span>
                      <SeverityBadge level={def.severity} />
                    </div>
                    <div className="text-xs text-[#00ff4180] mb-1">
                      <span className="text-[#00ff4140]">EVIDENCE:</span> {def.evidence}
                    </div>
                    <div className="text-xs text-[#00ff41]">
                      <span className="text-[#00ff4140]">FIX:</span> {def.fix}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pests & Disease */}
          {analysis.issues?.pests_disease?.length > 0 && (
            <div className="terminal-border rounded-lg p-4" style={{ borderColor: '#ff0000' }}>
              <h3 className="font-bold text-red-500 mb-3 flex items-center gap-2">
                🐛 PEST / DISEASE DETECTION
              </h3>
              <div className="space-y-3">
                {analysis.issues.pests_disease.map((issue, i) => (
                  <div key={i} className="bg-red-500/10 border border-red-500/40 rounded p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-bold text-sm text-red-400">{issue.type}</span>
                      <SeverityBadge level={issue.severity} />
                    </div>
                    <div className="text-xs text-[#00ff4180] mb-1">
                      <span className="text-[#00ff4140]">EVIDENCE:</span> {issue.evidence}
                    </div>
                    <div className="text-xs text-[#00ff41]">
                      <span className="text-[#00ff4140]">TREATMENT:</span> {issue.fix}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Plant Structure */}
          {analysis.structure && (
            <div className="terminal-border rounded-lg p-4">
              <h3 className="font-bold mb-3 flex items-center gap-2 terminal-glow">
                🌿 PLANT STRUCTURE
              </h3>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="bg-[#00ff4110] border border-[#00ff4140] rounded p-2 text-center">
                  <div className="text-[10px] text-[#00ff4180]">INTERNODE</div>
                  <div className="text-sm font-bold uppercase">{analysis.structure.internode_spacing}</div>
                </div>
                <div className="bg-[#00ff4110] border border-[#00ff4140] rounded p-2 text-center">
                  <div className="text-[10px] text-[#00ff4180]">CANOPY</div>
                  <div className="text-sm font-bold uppercase">{analysis.structure.canopy_evenness}</div>
                </div>
                <div className="bg-[#00ff4110] border border-[#00ff4140] rounded p-2 text-center">
                  <div className="text-[10px] text-[#00ff4180]">LIGHT</div>
                  <div className="text-sm font-bold uppercase">{analysis.structure.light_penetration}</div>
                </div>
              </div>
              {analysis.structure.notes && (
                <p className="text-xs text-[#00ff4180]">{analysis.structure.notes}</p>
              )}
            </div>
          )}

          {/* Training Recommendations */}
          {analysis.training && (
            <div className="terminal-border rounded-lg p-4" style={{ borderColor: '#9c27b0' }}>
              <h3 className="font-bold mb-3 flex items-center gap-2" style={{ color: '#9c27b0' }}>
                ✂️ TRAINING RECOMMENDATIONS
              </h3>

              {analysis.training.current_training_visible && (
                <div className="bg-[#9c27b020] border border-[#9c27b040] rounded p-2 mb-3 text-xs">
                  <span className="text-[#00ff4140]">CURRENT:</span>{' '}
                  <span className="font-bold">{analysis.training.current_training_visible}</span>
                </div>
              )}

              {analysis.training.recommendations?.length > 0 ? (
                <div className="space-y-3">
                  {analysis.training.recommendations.map((rec, i) => (
                    <div key={i} className="bg-[#9c27b010] border border-[#9c27b040] rounded p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-sm" style={{ color: '#9c27b0' }}>{rec.technique}</span>
                        <div className="flex gap-1">
                          <UrgencyBadge level={rec.urgency} />
                          <SeverityBadge level={rec.risk} />
                        </div>
                      </div>
                      <p className="text-xs mb-1">{rec.action}</p>
                      <div className="text-[10px] text-[#00ff4180]">
                        <span className="text-[#00ff4140]">WHY:</span> {rec.reason}
                      </div>
                      {rec.recovery_days > 0 && (
                        <div className="text-[10px] text-[#00ff4140] mt-1">
                          ⏱ Recovery: ~{rec.recovery_days} days
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-[#00ff4180]">No training recommended at this time.</p>
              )}

              {analysis.training.do_not_do && (
                <div className="mt-3 bg-red-500/10 border border-red-500/40 rounded p-2 text-xs">
                  <span className="font-bold text-red-400">⛔ DO NOT:</span>{' '}
                  <span className="text-[#00ff4180]">{analysis.training.do_not_do}</span>
                </div>
              )}
            </div>
          )}

          {/* All Clear */}
          {(!analysis.issues?.deficiencies?.length && !analysis.issues?.pests_disease?.length) && (
            <div className="terminal-border rounded-lg p-4 text-center">
              <div className="text-3xl mb-2">✅</div>
              <div className="font-bold terminal-glow">NO ISSUES DETECTED</div>
              <div className="text-xs text-[#00ff4180] mt-1">Plant looks healthy — keep it up!</div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between text-xs text-[#00ff4180]">
            <button
              onClick={() => setSelectedIndex(Math.min(selectedIndex + 1, inspections.length - 1))}
              disabled={selectedIndex >= inspections.length - 1}
              className={`px-3 py-1 rounded border ${
                selectedIndex >= inspections.length - 1
                  ? 'border-[#00ff4120] text-[#00ff4140] cursor-not-allowed'
                  : 'border-[#9c27b0] hover:bg-[#9c27b020] cursor-pointer'
              }`}
              style={selectedIndex < inspections.length - 1 ? { color: '#9c27b0' } : {}}
            >
              ← OLDER
            </button>
            <span>
              {selectedIndex + 1} of {inspections.length} inspections
            </span>
            <button
              onClick={() => setSelectedIndex(Math.max(selectedIndex - 1, 0))}
              disabled={selectedIndex <= 0}
              className={`px-3 py-1 rounded border ${
                selectedIndex <= 0
                  ? 'border-[#00ff4120] text-[#00ff4140] cursor-not-allowed'
                  : 'border-[#9c27b0] hover:bg-[#9c27b020] cursor-pointer'
              }`}
              style={selectedIndex > 0 ? { color: '#9c27b0' } : {}}
            >
              NEWER →
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default VisualInspectTab;
